import { Metamodel, DataFlowDSLAdapterRegistry, Owned } from 'shell-models-lib';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowExpressionUtils } from '../expression.util';
import { IntegerDataFlowType, BooleanDataFlowType, StringDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { BaseFormatAdapter } from './base-format.adapter';
import { AdobeCampaignSettings } from 'app/model/dataflow/dataset-settings/adobe-campaign-settings';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSchemaAdapterUtils } from '../dataflow-schema-adapter-utils';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(AdobeCampaignSettings)
})
export class AdobeCampaignFormatAdapter extends BaseFormatAdapter<AdobeCampaignSettings> {
    protected async toSourceDSL(settings: AdobeCampaignSettings): Promise<string[]> {
        const props = [];
        props.push(`format: '${DatasetFormatType.AdobeCampaign}'`);
        if (settings.resourceName.expression) {
            props.push(`resourceName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.resourceName)}`);
        }
        if (settings.batchSize.expression) {
            props.push(
                `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.batchSize,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (settings.requestInterval.expression) {
            props.push(
                `requestInterval: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.requestInterval,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (settings.filterColumn.expression && settings.filterValue.expression) {
            props.push(
                `filterMap: [${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.filterColumn
                )}->${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.filterValue)}]`
            );
        }
        return props;
    }

    protected async toSinkDSL(settings: AdobeCampaignSettings): Promise<string[]> {
        const props = [];
        props.push(`format: '${DatasetFormatType.AdobeCampaign}'`);
        if (settings.resourceName.expression) {
            props.push(`resourceName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.resourceName)}`);
        }
        if (settings.batchSize.expression) {
            props.push(
                `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.batchSize,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (settings.requestInterval.expression) {
            props.push(
                `requestInterval: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.requestInterval,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        props.push(
            `deletable:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                settings.allowDelete,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `insertable:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                settings.allowInsert,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `updateable:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                settings.allowUpdate,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `upsertable:${DataFlowExpressionUtils.wrapExpressionConfigValue(
                settings.allowUpsert,
                BooleanDataFlowType.TYPE
            )}`
        );
        const keyColumns = DataFlowSchemaAdapterUtils.getKeyColumnsProps(settings);
        if (keyColumns) {
            props.push(keyColumns);
        }
        const excludeColumns = settings.excludeColumns
            .filter((column) => column.expression)
            .map((col) => DataFlowExpressionUtils.wrapExpressionConfigValue(col, StringDataFlowType.TYPE, true))
            .join(',');
        if (!!excludeColumns && settings.allowUpsert) {
            props.push(`excludeFields:[${excludeColumns}]`);
        }

        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<AdobeCampaignSettings> {
        const settings = Owned.create<AdobeCampaignSettings>(AdobeCampaignSettings.MODEL, owner);
        settings.initialize(undefined);
        if (configs.has('resourceName')) {
            settings.resourceName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('resourceName'),
                owner
            );
            handledConfigs.push('resourceName');
        }
        if (configs.has('batchSize')) {
            settings.batchSize = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('batchSize'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('batchSize');
        }
        if (configs.has('requestInterval')) {
            settings.requestInterval = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('requestInterval'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('requestInterval');
        }
        if (configs.has('filterMap')) {
            const filterMap = configs.get('filterMap').slice(1, -1);
            settings.filterColumn = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                filterMap.split('->')[0],
                owner
            );
            settings.filterValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(filterMap.split('->')[1], owner);

            handledConfigs.push('filterMap');
        }

        return settings;
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<AdobeCampaignSettings> {
        const settings = Owned.create<AdobeCampaignSettings>(AdobeCampaignSettings.MODEL, owner);
        settings.initialize(undefined);
        if (configs.has('resourceName')) {
            settings.resourceName = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('resourceName'),
                owner
            );
            handledConfigs.push('resourceName');
        }
        if (configs.has('insertable')) {
            settings.allowInsert = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('insertable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('insertable');
        }
        if (configs.has('updateable')) {
            settings.allowUpdate = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('updateable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('updateable');
        }
        if (configs.has('upsertable')) {
            settings.allowUpsert = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('upsertable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('upsertable');
        }
        if (configs.has('deletable')) {
            settings.allowDelete = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('deletable'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('deletable');
        }
        if (configs.has('keys')) {
            const expression = configs.get('keys');
            settings.isListOfKeyColumns = expression.startsWith('[') && expression.endsWith(']');
            settings.keyColumns = settings.isListOfKeyColumns
                ? DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner, StringDataFlowType.TYPE, true)
                : [DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, 'string[]', true)];
            handledConfigs.push('keys');
        }
        if (configs.has('excludeFields')) {
            settings.excludeColumns = DataFlowExpressionUtils.parseExpressionArrayInDsl(
                configs.get('excludeFields'),
                owner,
                StringDataFlowType.TYPE,
                true
            );
            handledConfigs.push('excludeFields');
        }
        if (configs.has('batchSize')) {
            settings.batchSize = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('batchSize'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('batchSize');
        }
        if (configs.has('requestInterval')) {
            settings.requestInterval = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('requestInterval'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('requestInterval');
        }

        return settings;
    }
}
